/* ============================================================
   Bar Fridges Australia — Knowledge Base shared behaviour
   ------------------------------------------------------------
   Loaded once per KB page via <script src="{{ 'kb.js' | asset_url }}" defer>.
   Covers the two behaviours every KB page shares: scroll-reveal and
   the info-panel accordion. Page-specific scripts (noise slider, energy
   chart, building-in / mini-bar tab switchers) stay inline in their
   own page.<handle>.liquid template.
   All selectors are scoped to the `.kb` wrapper so nothing on the
   host theme is affected.
   ============================================================ */
(function () {
  function init() {
    // Reveal immediately for no-JS fallback users the moment JS confirms it runs.
    document.querySelectorAll('.kb.no-js').forEach(function (el) {
      el.classList.remove('no-js');
    });

    var revealEls = document.querySelectorAll('.kb .reveal');

    /* ── Scroll-reveal ── */
    if ('IntersectionObserver' in window) {
      var obs = new IntersectionObserver(function (entries) {
        entries.forEach(function (entry) {
          if (entry.isIntersecting) {
            entry.target.classList.add('visible');
            entry.target.querySelectorAll('.bar-fill[data-w], .bar-energy[data-w]').forEach(function (b) {
              b.style.width = b.dataset.w;
            });
            obs.unobserve(entry.target);
          }
        });
      }, { threshold: 0.08 });
      revealEls.forEach(function (el) { obs.observe(el); });
    } else {
      // Old browser: just show everything and fill any animated bars.
      revealEls.forEach(function (el) {
        el.classList.add('visible');
        el.querySelectorAll('.bar-fill[data-w], .bar-energy[data-w]').forEach(function (b) {
          b.style.width = b.dataset.w;
        });
      });
    }

    /* ── Info-panel accordion ── */
    document.querySelectorAll('.kb .panel').forEach(function (p) {
      p.addEventListener('click', function () { p.classList.toggle('open'); });
    });

    /* ── Mobile spec selector ──
       Turns a wide comparison table into a "pick one, see its details" card
       on small screens (shown ≤700px via .specsel CSS). Progressive
       enhancement: the table stays the single source of truth and is only
       hidden once we mark it .specsel-on, so if this never runs the table
       still shows (its CSS row→card fallback keeps it readable). Mark a
       table with one of:
         data-specsel="by-row"     each tbody row is an option (lookup tables)
         data-specsel="by-column"  each data column is an option (comparison matrix) */
    document.querySelectorAll('.kb table[data-specsel]').forEach(buildSpecSelect);

    /* ── Knowledge-base search ── */
    buildSearch();

    /* ── Left hierarchy sidebar ── */
    buildSidebar();

    /* ── Back to top button ── */
    buildBackToTop();
  }

  /* ── Back to top button ──
     A fixed circular button that fades in once the reader scrolls down and
     smooth-scrolls to the top. Appended to <body> so it's never clipped by a
     scroll container; hidden in print via kb.css. */
  function buildBackToTop() {
    if (!document.querySelector('.kb') || document.querySelector('.kb-totop')) return;
    var btn = document.createElement('button');
    btn.type = 'button';
    btn.className = 'kb-totop';
    btn.setAttribute('aria-label', 'Back to top');
    btn.innerHTML = '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M12 19V5M5 12l7-7 7 7"/></svg>';
    btn.addEventListener('click', function () { window.scrollTo({ top: 0, behavior: 'smooth' }); });
    document.body.appendChild(btn);
    function toggle() { btn.classList.toggle('show', window.pageYOffset > 600); }
    window.addEventListener('scroll', toggle, { passive: true });
    toggle();
  }

  /* ── Left hierarchy sidebar ──
     Builds a sticky left rail listing every KB page; the current page expands
     to show its <h2> sub-sections (auto-derived) with smooth-scroll + scroll-spy.
     Replaces the old bottom Prev/Next buttons. Sticky (not fixed) so it stays
     visible while scrolling but never overlaps the theme header. Page list and
     styling live here + in kb.css. */
  var NAV_PAGES = [
    { url: '/pages/support',  title: 'Support Hub',          group: 'top' },
    { url: '/blogs/knowledgebase', title: 'Knowledge Base',  group: 'top' },
    { url: '/pages/noise-levels',           title: 'Noise Levels',         group: 'guide' },
    { url: '/pages/condensation',    title: 'Condensation',         group: 'guide' },
    { url: '/pages/energy-consumption',          title: 'Energy Consumption',   group: 'guide' },
    { url: '/pages/fridge-sunlight',        title: 'Sunlight & LOW E',     group: 'guide' },
    { url: '/pages/building-in-bible',     title: 'Building In Bible',    group: 'guide' },
    { url: '/pages/stainless-steel', title: 'Stainless Steel Care', group: 'guide' },
    { url: '/pages/mini-bar-fridge-specifications',        title: 'Mini Bar Fridges',     group: 'guide' }
  ];

  function navSlug(t) { return t.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '').slice(0, 60); }
  // Document-absolute top via the offsetTop chain — ignores CSS transforms, so
  // the .reveal slide-in animation can't throw the scroll target off.
  function absTop(el) { var y = 0; while (el) { y += el.offsetTop; el = el.offsetParent; } return y; }
  // Section headings: main <h2>, group .section-heading/.section-label labels,
  // and the closing "Key Takeaway" in .highlight-box.
  var HEADING_SEL = 'h2, .section-heading, .section-label, .highlight-box h3';
  function navLabel(el) { return (el.textContent || '').replace(/^[\s▶●◆•\-–—]+/, '').replace(/\s+/g, ' ').trim(); }

  // Height of the theme's sticky/fixed header (so the rail starts below it,
  // not behind it). Walks up from the top-centre point to a fixed/sticky
  // ancestor. Returns 0 if the header isn't pinned (then the CSS default applies).
  function detectHeaderHeight() {
    var max = 0, k, el, s, r;
    // Probe a few points across the top for a pinned (fixed/sticky) header,
    // walking up to the pinned ancestor. Use its bottom edge (handles a stacked
    // announcement bar + header).
    var xs = [Math.round(window.innerWidth / 2), 40, Math.max(0, window.innerWidth - 40)];
    for (k = 0; k < xs.length; k++) {
      el = document.elementFromPoint(xs[k], 5);
      while (el && el !== document.body && el !== document.documentElement) {
        s = window.getComputedStyle(el);
        if (s.position === 'fixed' || s.position === 'sticky') {
          r = el.getBoundingClientRect();
          if (r.top <= 1 && r.height > 0 && r.height < 320 && r.bottom > max) max = r.bottom;
          break;
        }
        el = el.parentElement;
      }
    }
    // Fallback: scan likely header elements directly.
    if (!max) {
      var nodes = document.querySelectorAll('header, [class*="header"], [class*="Header"], #shopify-section-header');
      for (k = 0; k < nodes.length; k++) {
        s = window.getComputedStyle(nodes[k]);
        if (s.position === 'fixed' || s.position === 'sticky') {
          r = nodes[k].getBoundingClientRect();
          if (r.top <= 1 && r.height > 0 && r.height < 320 && r.bottom > max) max = r.bottom;
        }
      }
    }
    return Math.round(max);
  }

  function buildSidebar() {
    var container = document.querySelector('.kb .container');
    if (!container || document.querySelector('.kb-sidebar')) return;

    var path = location.pathname.replace(/\/+$/, '');
    var cur = '';
    NAV_PAGES.forEach(function (p) { if (path === p.url || path.indexOf(p.url) !== -1) cur = p.url; });

    document.querySelectorAll('.kb .next-btn').forEach(function (n) { n.remove(); });

    var headerH = detectHeaderHeight();
    var scrollOff = headerH + 20;

    var sections = [], used = {};
    container.querySelectorAll(HEADING_SEL).forEach(function (h) {
      if (h.offsetParent === null) return; // skip headings hidden at this breakpoint
      var label = navLabel(h);
      if (!label) return;
      var id = h.id;
      if (!id) {
        id = navSlug(label) || 'section';
        var base = id, n = 2;
        while (used[id] || document.getElementById(id)) { id = base + '-' + n++; }
        h.id = id;
      }
      used[id] = true;
      sections.push({ id: id, label: label, el: h });
    });
    // The hub is just a launcher — its <h2>s are card titles, not sections.
    if (cur === '/pages/support' || location.pathname.indexOf('/pages/knowledge-base') !== -1) sections = [];

    var nav = document.createElement('nav');
    nav.className = 'kb-sidebar';
    nav.setAttribute('aria-label', 'Guide navigation');

    var mtoggle = document.createElement('button');
    mtoggle.type = 'button';
    mtoggle.className = 'kb-sb-mtoggle';
    mtoggle.innerHTML = '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M4 6h16M4 12h16M4 18h16"/></svg><span>Bar Fridges Australia Guides</span>';
    mtoggle.addEventListener('click', function () { nav.classList.toggle('open'); });
    nav.appendChild(mtoggle);

    var body = document.createElement('div');
    body.className = 'kb-sb-body';
    var brand = document.createElement('a');
    brand.className = 'kb-sb-brand';
    brand.href = '/pages/support';
    brand.innerHTML = '<b>Bar Fridges Australia Guides</b>';
    body.appendChild(brand);
    var list = document.createElement('ul');
    list.className = 'kb-sb-list';
    body.appendChild(list);
    nav.appendChild(body);

    var slinks = {}, addedLabel = false;
    NAV_PAGES.forEach(function (pg) {
      if (pg.group === 'guide' && !addedLabel) {
        var gl = document.createElement('li');
        gl.className = 'kb-sb-grouplabel';
        gl.textContent = 'Buying & Care Guides';
        list.appendChild(gl);
        addedLabel = true;
      }
      var li = document.createElement('li');
      li.className = 'kb-sb-item';
      var isCur = pg.url === cur;
      if (isCur) li.className += ' active';
      var a = document.createElement('a');
      a.className = 'kb-sb-link';
      a.href = pg.url;
      var sp = document.createElement('span');
      sp.textContent = pg.title;
      a.appendChild(sp);
      if (isCur && sections.length) {
        a.insertAdjacentHTML('beforeend',
          '<svg class="kb-sb-chev" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M9 6l6 6l-6 6"/></svg>');
      }
      li.appendChild(a);
      if (isCur && sections.length) {
        var sub = document.createElement('ul');
        sub.className = 'kb-sb-sections';
        sections.forEach(function (s) {
          var sli = document.createElement('li');
          var sa = document.createElement('a');
          sa.href = '#' + s.id;
          sa.textContent = s.label;
          sa.setAttribute('data-id', s.id);
          sa.addEventListener('click', function (e) {
            e.preventDefault();
            var t = document.getElementById(s.id);
            if (t) {
              window.scrollTo({ top: Math.max(0, absTop(t) - scrollOff), behavior: 'smooth' });
              if (history.replaceState) history.replaceState(null, '', '#' + s.id);
            }
            nav.classList.remove('open');
          });
          sli.appendChild(sa);
          sub.appendChild(sli);
          slinks[s.id] = sa;
        });
        li.appendChild(sub);
      }
      list.appendChild(li);
    });

    var shell = document.createElement('div');
    shell.className = 'kb-shell';
    container.parentNode.insertBefore(shell, container);
    shell.appendChild(nav);
    shell.appendChild(container);

    // Offset the sticky rail below the theme's pinned header.
    function applyTop() {
      var h = detectHeaderHeight();
      scrollOff = h + 20;
      if (h) {
        nav.style.top = (h + 16) + 'px';
        nav.style.maxHeight = 'calc(100vh - ' + (h + 32) + 'px)';
      } else {
        nav.style.top = '';
        nav.style.maxHeight = '';
      }
    }
    applyTop();
    setTimeout(applyTop, 300);
    setTimeout(applyTop, 1200);
    window.addEventListener('load', applyTop);
    window.addEventListener('resize', applyTop, { passive: true });

    if (sections.length) {
      var ticking = false;
      var spy = function () {
        ticking = false;
        var line = window.pageYOffset + scrollOff + 12;
        var atBottom = (window.innerHeight + window.pageYOffset) >= (document.documentElement.scrollHeight - 2);
        var active = null, firstVisible = null, lastVisible = null;
        for (var i = 0; i < sections.length; i++) {
          var el = sections[i].el;
          if (el.offsetParent === null) continue; // hidden at this breakpoint
          if (!firstVisible) firstVisible = sections[i];
          lastVisible = sections[i];
          if (absTop(el) <= line) active = sections[i];
        }
        if (!active) active = firstVisible;
        // At the page bottom the last heading often can't reach the line — pin it.
        if (atBottom && lastVisible) active = lastVisible;
        for (var id in slinks) {
          if (slinks.hasOwnProperty(id)) slinks[id].classList.toggle('active', !!active && id === active.id);
        }
      };
      window.addEventListener('scroll', function () {
        if (!ticking) { ticking = true; window.requestAnimationFrame(spy); }
      }, { passive: true });
      spy();
    }
  }

  function buildSpecSelect(table) {
    var mode = table.getAttribute('data-specsel');
    var headCells = table.querySelectorAll('thead th');
    var bodyRows = table.querySelectorAll('tbody tr');
    if (!bodyRows.length || !headCells.length) return;

    var options = []; // { label, cls, titleHtml?, render() -> [{ term, html }] }

    if (mode === 'by-column') {
      // First column is the row label ("Feature"); each later column is an option.
      headCells.forEach(function (th, c) {
        if (c === 0) return;
        options.push({
          label: th.textContent.trim(),
          cls: th.className,
          render: function () {
            return Array.prototype.map.call(bodyRows, function (tr) {
              var cells = tr.children;
              return { term: cells[0].innerHTML, html: cells[c] ? cells[c].innerHTML : '' };
            });
          }
        });
      });
    } else { // by-row
      bodyRows.forEach(function (tr) {
        var cells = tr.children;
        var first = cells[0];
        var strong = first.querySelector('strong');
        options.push({
          label: (strong ? strong.textContent : first.textContent).trim(),
          cls: '',
          titleHtml: first.innerHTML,
          render: function () {
            var pairs = [];
            for (var i = 1; i < cells.length; i++) {
              pairs.push({ term: headCells[i] ? headCells[i].textContent.trim() : '', html: cells[i].innerHTML });
            }
            return pairs;
          }
        });
      });
    }
    if (!options.length) return;

    var wrap = document.createElement('div');
    wrap.className = 'specsel';

    // A short list of short labels reads best as a segmented control; anything
    // longer would crowd the buttons, so fall back to a dropdown.
    var useTabs = options.length <= 4 && options.every(function (o) { return o.label.length <= 16; });
    var panel = document.createElement('div');
    panel.className = 'specsel-panel';

    function show(i) {
      var o = options[i];
      var html = o.titleHtml ? '<div class="specsel-title">' + o.titleHtml + '</div>' : '';
      html += '<dl class="specsel-dl">';
      o.render().forEach(function (p) {
        html += '<div class="specsel-pair"><dt>' + p.term + '</dt><dd>' + p.html + '</dd></div>';
      });
      panel.innerHTML = html + '</dl>';
      if (useTabs) {
        control.querySelectorAll('.specsel-tab').forEach(function (b, bi) {
          b.classList.toggle('active', bi === i);
          if (bi === i) b.setAttribute('aria-selected', 'true');
          else b.removeAttribute('aria-selected');
        });
      }
    }

    var control;
    if (useTabs) {
      control = document.createElement('div');
      control.className = 'specsel-tabs';
      control.setAttribute('role', 'tablist');
      options.forEach(function (o, i) {
        var b = document.createElement('button');
        b.type = 'button';
        b.className = 'specsel-tab' + (o.cls ? ' ' + o.cls : '');
        b.setAttribute('role', 'tab');
        b.textContent = o.label;
        b.addEventListener('click', function () { show(i); });
        control.appendChild(b);
      });
    } else {
      control = document.createElement('select');
      control.className = 'specsel-select';
      control.setAttribute('aria-label', 'Choose an option to view');
      options.forEach(function (o, i) {
        var opt = document.createElement('option');
        opt.value = i;
        opt.textContent = o.label;
        control.appendChild(opt);
      });
      control.addEventListener('change', function () { show(+control.value); });
    }

    wrap.appendChild(control);
    wrap.appendChild(panel);
    show(0);
    table.classList.add('specsel-on');
    table.insertAdjacentElement('afterend', wrap);
  }

  /* ── Knowledge-base search ──
     Client-side search over a small, hand-maintained index — no database,
     no build step. The box is injected at the top of every KB page's
     `.container`; results link to the matching guide. When a page's focus
     changes materially, update its entry below (summary + keywords). */
  var KB_INDEX = [
    { title: 'Knowledge Base', url: '/blogs/knowledgebase',
      summary: 'The full Bar Fridges Australia knowledge base — articles and guides covering common questions, product care and troubleshooting.',
      keywords: ['knowledge base','knowledgebase','blog','articles','guides','help','questions'] },
    { title: 'Building In Bible', url: '/pages/building-in-bible',
      summary: 'Building fridges into cabinetry — front vs non-front venting, side, top and rear clearances, 200cm² vents, the chimney effect and power-lead allowance.',
      keywords: ['building in','build in','built in','cabinetry','cabinet','vent','venting','ventilation','clearance','chimney','alfresco','front venting','rear venting','grill','grille','undercounter','flush','install','installation','platform','recess','gap','power lead','power cord','warranty','compressor','checklist'] },
    { title: 'Condensation on Glass Door Fridges', url: '/pages/condensation',
      summary: 'Why glass door fridges sweat in humid areas and how to stop it — dual, triple, Low-E and heated glass, humidity thresholds and argon gas.',
      keywords: ['condensation','sweating','sweat','water','moisture','humidity','humid','glass door','fog','fogging','dew point','dual glazed','triple glazed','low e','lowe','heated glass','argon','drips','wet'] },
    { title: 'Energy Consumption', url: '/pages/energy-consumption',
      summary: 'Running costs and power use of glass door fridges — what drives energy consumption and how to keep the bills down.',
      keywords: ['energy','running cost','running costs','power','electricity','bill','bills','efficiency','efficient','consumption','kwh','star rating','watts','economical','cost to run','compressor','fan','fans','lighting','led','meps','energy rating','energy label','ambient','test data','balance'] },
    { title: 'Sunlight, LOW E Glass & IP Ratings', url: '/pages/fridge-sunlight',
      summary: 'How direct sun overheats fridges, how Low-E glass reflects heat, glass U-values, and what IP (ingress protection) ratings really mean.',
      keywords: ['sunlight','sun','direct sun','uv','low e','lowe','heat','overheat','overheating','ip rating','ip','ingress protection','waterproof','water resistant','weatherproof','outdoor','alfresco','shade','u value','solid door','glass door','pergola','covered alfresco','open alfresco','rain','humidity','west facing','kitchen','tropical'] },
    { title: 'Noise Levels', url: '/pages/noise-levels',
      summary: 'How loud bar fridges are — decibels explained, what is quiet enough for living areas and bedrooms, and what causes compressor noise.',
      keywords: ['noise','noisy','sound','loud','quiet','silent','decibel','db','dba','compressor','hum','humming','buzzing','vibration','rattle','bedroom','fan','fans','condenser fan','evaporator fan','alfresco','commercial','kitchen','garage','cavity','acoustic foam','chart','video','popping','cracking','grinding','knocking','scraping','normal','level','levelling','leveling','legs','sound absorbing','soundproofing','soundproof','alcove','carpet','mineral wool','plasterboard'] },
    { title: 'Care & Protection of Stainless Steel', url: '/pages/stainless-steel',
      summary: 'Looking after stainless steel fridges — grades for outdoor and coastal use (304 vs 430 vs 316), the oil protection method, preventing rust and tea-staining, and cleaning.',
      keywords: ['stainless steel','stainless','steel','rust','rusting','corrosion','tea stain','tea staining','304','430','316','marine grade','coastal','salt','sea','cleaning','clean','fingerprints','outdoor','finish','oil','olive oil','coconut oil','commercial cleaner','oil method','oil protection method','oil treatment','maintenance schedule'] },
    { title: 'Mini Bar Fridges — Accommodation', url: '/pages/mini-bar-fridge-specifications',
      summary: 'Minibars for hotels and motels — silent absorption vs compressor models, where each suits, and their venting and build-in needs.',
      keywords: ['mini bar','minibar','mini-bar','hotel','motel','accommodation','absorption','silent','quiet','compressor','guest','room','airbnb','bnb','heat pipe','heatpipe','ambient','cooling','chimney','venting','vent','clearance','energy','kwh','lock','reversible door','tropical','joinery'] }
  ];

  function buildSearch() {
    var container = document.querySelector('.kb .container');
    if (!container || container.querySelector('.kb-search')) return;

    var wrap = document.createElement('div');
    wrap.className = 'kb-search';
    wrap.innerHTML =
      '<div class="kb-search-box">' +
        '<svg class="kb-search-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><circle cx="11" cy="11" r="7"></circle><line x1="21" y1="21" x2="16.65" y2="16.65"></line></svg>' +
        '<input type="search" class="kb-search-input" placeholder="Search the Buying &amp; Care Guides..." aria-label="Search the Buying &amp; Care Guides" autocomplete="off">' +
        '<button type="button" class="kb-search-clear" aria-label="Clear search" hidden>&times;</button>' +
      '</div>' +
      '<div class="kb-search-results" role="listbox" hidden></div>';
    container.insertBefore(wrap, container.firstChild);

    var input = wrap.querySelector('.kb-search-input');
    var clearBtn = wrap.querySelector('.kb-search-clear');
    var results = wrap.querySelector('.kb-search-results');
    var active = -1;

    function esc(s) {
      return String(s).replace(/[&<>"]/g, function (c) {
        return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' }[c];
      });
    }
    function highlight(text, tokens) {
      var out = esc(text);
      tokens.forEach(function (t) {
        if (t.length < 2) return;
        var re = new RegExp('(' + t.replace(/[.*+?^${}()|[\]\\]/g, '\\$&') + ')', 'ig');
        out = out.replace(re, '<mark>$1</mark>');
      });
      return out;
    }
    function rank(q) {
      var tokens = q.toLowerCase().split(/\s+/).filter(Boolean);
      if (!tokens.length) return [];
      return KB_INDEX.map(function (e) {
        var title = e.title.toLowerCase();
        var kw = e.keywords.join(' ').toLowerCase();
        var sum = e.summary.toLowerCase();
        var score = 0;
        tokens.forEach(function (t) {
          if (title.indexOf(t) === 0) score += 4;
          if (title.indexOf(t) !== -1) score += 10;
          if (kw.indexOf(t) !== -1) score += 6;
          if (sum.indexOf(t) !== -1) score += 2;
        });
        return { e: e, score: score };
      }).filter(function (r) { return r.score > 0; })
        .sort(function (a, b) { return b.score - a.score; })
        .slice(0, 7);
    }
    function render() {
      var q = input.value.trim();
      var tokens = q.toLowerCase().split(/\s+/).filter(Boolean);
      active = -1;
      clearBtn.hidden = !q;
      if (!q) { results.hidden = true; results.innerHTML = ''; return; }
      var hits = rank(q);
      if (!hits.length) {
        results.innerHTML = '<div class="kb-search-empty">No matches for &ldquo;' + esc(q) + '&rdquo;. Try &ldquo;venting&rdquo;, &ldquo;condensation&rdquo; or &ldquo;noise&rdquo;.</div>';
      } else {
        results.innerHTML = hits.map(function (r) {
          return '<a class="kb-search-result" href="' + r.e.url + '">' +
            '<div class="r-title">' + highlight(r.e.title, tokens) + '</div>' +
            '<div class="r-sum">' + highlight(r.e.summary, tokens) + '</div>' +
          '</a>';
        }).join('');
      }
      results.hidden = false;
    }
    function setActive(i) {
      var items = results.querySelectorAll('.kb-search-result');
      if (!items.length) return;
      active = (i + items.length) % items.length;
      items.forEach(function (el, idx) { el.classList.toggle('active', idx === active); });
      items[active].scrollIntoView({ block: 'nearest' });
    }

    input.addEventListener('input', render);
    input.addEventListener('focus', function () { if (input.value.trim()) render(); });
    input.addEventListener('keydown', function (e) {
      var items = results.querySelectorAll('.kb-search-result');
      if (e.key === 'ArrowDown') { e.preventDefault(); setActive(active + 1); }
      else if (e.key === 'ArrowUp') { e.preventDefault(); setActive(active - 1); }
      else if (e.key === 'Enter') {
        var target = (active >= 0 && items[active]) ? items[active] : items[0];
        if (target) window.location.href = target.getAttribute('href');
      } else if (e.key === 'Escape') { input.value = ''; render(); input.blur(); }
    });
    clearBtn.addEventListener('click', function () { input.value = ''; render(); input.focus(); });
    document.addEventListener('click', function (e) { if (!wrap.contains(e.target)) results.hidden = true; });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
